/**
 * DB Manager connections follow the project scope (card 5.3). Its tree runs in
 * the guest iframe and draws connections in the order the host hands them
 * (`db.listSavedConnections`), so the host orders them — the active project's
 * first, the rest after, nothing dropped (owner decision 0.3) — and tells the
 * guest to re-read whenever the project or its links change.
 *
 * Adding a DB connection to a project: the Projects view / Graph (any node),
 * and a connection saved while a project is active joins it (hostBridge's
 * `db.saveConnection`).
 */
import { useProjectScopeStore } from './projectScopeStore';
import { currentFilterScope } from './projectMembership';
import { sortProjectFirst } from './projectScopeFilter';
import { KIND_DB, connectionNodeId } from './connectionProjectGroups';
import { addNewItemToActiveProject } from './projectItems';

/** Pure: the descriptors with the scope's connections first (stable). */
export function orderDbConnections(descriptors = [], scope = currentFilterScope()) {
  return sortProjectFirst(descriptors, scope, (d) => ({ nodeId: connectionNodeId(KIND_DB, d) }));
}

/**
 * A NEW DB connection saved while a project is active joins it (decision
 * 0.2); an edit of a stored one (`origin: 'vault'` with an id) is left where
 * it is. Never throws.
 */
export function joinActiveProjectIfNew(id, descriptor) {
  const isNew = !(descriptor?.origin === 'vault' && descriptor?.id);
  if (id && isNew) return addNewItemToActiveProject(connectionNodeId(KIND_DB, { id }), 'personal');
  return Promise.resolve(false);
}

/**
 * Calls `notify()` when the active project or the membership links change.
 * Returns the unsubscribe function.
 */
export function onProjectScopeChange(notify) {
  let last = useProjectScopeStore.getState();
  return useProjectScopeStore.subscribe((s) => {
    const changed = s.activeProjectId !== last.activeProjectId || s.known.links !== last.known.links || s.status !== last.status;
    last = s;
    if (changed) notify();
  });
}
